<template>
  <div class="auto-calculator-edit">
    <div class="editor-header">
      <div class="header-title">
        <h2>
          <i class="bx bxs-truck"></i>
          Редактор калькулятора автомобильных перевозок
        </h2>
        <div v-if="updatedAt" class="last-updated">
          <i class="bx bx-time"></i>
          Последнее обновление: {{ formatDate(updatedAt) }}
        </div>
      </div>
      <div class="editor-actions">
        <button 
          class="btn btn-success"
          @click="addFtlTariff"
        >
          <i class="bx bx-plus"></i>
          Добавить FTL тариф
        </button>
        <button 
          class="btn btn-secondary"
          @click="$emit('close-editor')"
        >
          <i class="bx bx-x"></i>
          Закрыть редактор
        </button>
        <button 
          class="btn btn-primary"
          @click="saveCalculator"
          :disabled="isSaving"
        >
          <i class="bx bx-save"></i>
          {{ isSaving ? 'Сохранение...' : 'Сохранить' }}
        </button>
      </div>
    </div>
    
    <div class="editor-content">
      <!-- FTL калькулятор -->
      <div class="calculator-section">
        <h3>
          <i class="bx bx-package"></i>
          FTL (Full Truck Load) - Полная загрузка
        </h3>
        
        <div class="ftl-table-container">
          <!-- Сообщение когда нет тарифов -->
          <div v-if="ftlTariffs.length === 0" class="empty-tariffs-message">
            <i class="bx bx-info-circle"></i>
            <p>Пока нет данных о FTL тарифах</p>
          </div>
          
          <!-- Таблица FTL тарифов -->
          <table v-else class="ftl-table">
            <thead>
              <tr>
                <th>Terms</th>
                <th>Город отправления</th>
                <th>POD (Пункт назначения)</th>
                <th>Евро-тент</th>
                <th style="text-align: center;">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(tariff, index) in ftlTariffs" :key="index">
                <td>
                  <input 
                    v-model="tariff.terms" 
                    type="text" 
                    class="form-control"
                    placeholder="EXW"
                  />
                </td>
                <td>
                  <input 
                    v-model="tariff.origin_city" 
                    type="text" 
                    class="form-control"
                    placeholder="Шанхай /Shanghai"
                  />
                </td>
                <td>
                  <input 
                    v-model="tariff.pod" 
                    type="text" 
                    class="form-control"
                    placeholder="Москва"
                  />
                </td>
                <td>
                  <input 
                    v-model.number="tariff.euro_tent" 
                    type="number" 
                    class="form-control"
                    step="0.01"
                    min="0"
                    placeholder="1000"
                  />
                </td>
                <td style="text-align: center;">
                  <button 
                    v-if="!tariff.is_default"
                    class="btn btn-danger btn-sm"
                    @click="removeFtlTariff(index)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>
                  <span v-else class="text-muted">Системный</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- LTL калькулятор -->
      <div class="calculator-section">
        <h3>
          <i class="bx bx-box"></i>
          LTL (Less than Truck Load) - Частичная загрузка
        </h3>
        
        <div class="ltl-settings">
          <div class="ltl-item">
            <label>Ставка/м3</label>
            <input 
              v-model.number="ltlSettings.rate_per_m3" 
              type="number" 
              class="form-control"
              step="0.01"
              min="0"
            />
          </div>
          <div class="ltl-item">
            <label>DTHC/BL</label>
            <input 
              v-model.number="ltlSettings.dthc_bl" 
              type="number" 
              class="form-control"
              step="0.01"
              min="0"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { API_CALCULATOR_SETTINGS_AUTO } from '@/constants'

export default {
  name: 'AutoCalculatorEdit',
  
  data() {
    return {
      isSaving: false,
      isLoading: true,
      ftlTariffs: [],
      ltlSettings: {
        rate_per_m3: 0,
        dthc_bl: 0
      },
      updatedAt: null
    }
  },
  
  async mounted() {
    await this.loadSettings()
  },
  
  methods: {
    async loadSettings() {
      try {
        this.isLoading = true
        const response = await axios.get(API_CALCULATOR_SETTINGS_AUTO)
        
        if (response.data.success && response.data.data) {
          this.ftlTariffs = response.data.data.ftl_tariffs || []
          this.ltlSettings = response.data.data.ltl_settings || {
            rate_per_m3: 0,
            dthc_bl: 0
          }
          this.updatedAt = response.data.updated_at
        } else {
          this.ftlTariffs = []
          this.ltlSettings = {
            rate_per_m3: 0,
            dthc_bl: 0
          }
          this.updatedAt = null
        }
      } catch (error) {
        console.error('Ошибка загрузки настроек:', error)
        this.ftlTariffs = []
        this.ltlSettings = {
          rate_per_m3: 0,
          dthc_bl: 0
        }
        this.$toast.error('Ошибка загрузки настроек калькулятора')
      } finally {
        this.isLoading = false
      }
    },
    
    addFtlTariff() {
      this.ftlTariffs.push({
        terms: '',
        origin_city: '',
        pod: '',
        euro_tent: 0,
        is_default: false
      })
    },
    
    removeFtlTariff(index) {
      this.ftlTariffs.splice(index, 1)
    },
    
    async saveCalculator() {
      try {
        this.isSaving = true
        
        const settings = {
          ftl_tariffs: this.ftlTariffs,
          ltl_settings: this.ltlSettings
        }
        
        const response = await api.call('put', API_CALCULATOR_SETTINGS_AUTO, { settings })
        
        if (response.data.success) {
          this.updatedAt = response.data.updated_at
          this.$toast.success('Калькулятор авто перевозок успешно сохранен')
        } else {
          this.$toast.error(response.data.message || 'Ошибка сохранения')
        }
      } catch (error) {
        console.error('Ошибка сохранения:', error)
        this.$toast.error('Ошибка сохранения настроек калькулятора')
      } finally {
        this.isSaving = false
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.auto-calculator-edit {
  overflow: hidden;
  
  .editor-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 15px 0px 30px 0;
    
    .header-title {
      display: flex;
      flex-direction: column;
      gap: 8px;
      
      h2 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: #0d6348;
        
        i {
          font-size: 24px;
        }
      }
      
      .last-updated {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: #6c757d;
        font-style: italic;
        
        i {
          font-size: 14px;
        }
      }
    }
    
    .editor-actions {
      display: flex;
      gap: 8px;
      
      .btn {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        border: none;
        border-radius: 4px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        font-size: 12px;
        
        i {
          font-size: 14px;
        }
        
        &.btn-primary {
          background: #e37926;
          color: white;
          border: 1px solid #d16a1a;
          
          &:hover:not(:disabled) {
            background: #d16a1a;
          }
          
          &:disabled {
            background: rgba(227, 121, 38, 0.6);
            cursor: not-allowed;
          }
        }
        
        &.btn-secondary {
          background: transparent;
          color: #0d6348;
          border: 1px solid #0d6348;
          
          &:hover {
            background: #0d6348;
            color: white;
          }
        }
      }
    }
  }
  
  .editor-content {
    padding: 20px;
    
    .calculator-section {
      margin-bottom: 30px;
      
      h3 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 15px 0;
        color: #0d6348;
        font-size: 18px;
        font-weight: 600;
        
        i {
          font-size: 20px;
        }
      }
      
      .ftl-table-container {
        overflow-x: auto;
        margin-bottom: 20px;
        border-radius: 6px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        
        .empty-tariffs-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          background: #f8f9fa;
          border-radius: 6px;
          color: #6c757d;
          
          i {
            font-size: 36px;
            margin-bottom: 12px;
            color: #0d6348;
          }
          
          p {
            margin: 0;
            font-size: 14px;
            font-weight: 500;
          }
        }
        
        .ftl-table {
          width: 100%;
          border-collapse: collapse;
          background: #fff;
          min-width: 500px;
          
          th {
            background: #0d6348;
            color: white;
            font-weight: 600;
            padding: 8px 6px;
            text-align: left;
            border: 1px solid #0a4d3a;
            font-size: 11px;
            white-space: nowrap;
          }
          
          td {
            padding: 4px 6px;
            border: 1px solid #dee2e6;
            vertical-align: middle;
            
            .form-control {
              width: 100%;
              padding: 4px 6px;
              border: 1px solid #ced4da;
              border-radius: 3px;
              font-size: 11px;
              
              &:focus {
                outline: none;
                border-color: #0d6348;
                box-shadow: 0 0 0 2px rgba(13, 99, 72, 0.25);
              }
            }
            
            .btn {
              padding: 3px 6px;
              font-size: 11px;
              border-radius: 3px;
              
              &.btn-danger {
                background: #dc3545;
                color: white;
                border: none;
                
                &:hover {
                  background: #c82333;
                }
              }
            }
            
            .text-muted {
              color: #6c757d;
              font-size: 12px;
              font-style: italic;
            }
          }
        }
      }
      
      .ltl-settings {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 15px;
        
        .ltl-item {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 6px;
          border: 1px solid #e9ecef;
          
          label {
            display: block;
            margin-bottom: 8px;
            font-size: 12px;
            font-weight: 600;
            color: #0d6348;
          }
          
          .form-control {
            width: 100%;
            padding: 6px 8px;
            border: 1px solid #ced4da;
            border-radius: 4px;
            font-size: 12px;
            
            &:focus {
              outline: none;
              border-color: #0d6348;
              box-shadow: 0 0 0 2px rgba(13, 99, 72, 0.25);
            }
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .auto-calculator-edit {
    .editor-header {
      flex-direction: column;
      gap: 15px;
      text-align: center;
      
      .editor-actions {
        width: 100%;
        justify-content: center;
      }
    }
  }
}
</style>
